import React, { useEffect, useState } from 'react';
import { animated, useSpring } from '@react-spring/web'; // Ensure correct import
import useOverlayStore from '@core/stores/useOverlayStore';
import styles from './Overlay.module.scss';

export const overlayStateKey = 'overlay';

/**
 * Overlay component that manages the visibility of an overlay.
 * It fades in and out based on the state of the overlay (`overlayOpen`) and manages the zIndex of the overlay.
 * When the overlay is clicked, it triggers the `setOverlayState` function to close it.
 *
 * @returns The overlay element, or null if not visible.
 */
export const Overlay: React.FC = () => {
    const { setOverlayState, overlayOpen, zIndex } = useOverlayStore();
    const [overlayShow, setOverlayShow] = useState(false);

    // React Spring hook to manage opacity animation for the overlay
    const [{ opacity }, springRef] = useSpring(() => ({
        opacity: 0,
        config: { duration: 300 },
    }));

    /**
     * Effect that listens for changes to `overlayOpen`.
     * It triggers the fade-in or fade-out animation and sets visibility for the overlay.
     */
    useEffect(() => {
        if (overlayOpen) {
            springRef.start({ opacity: 1 }); // Fade in
        } else {
            springRef.start({ opacity: 0 }); // Fade out
        }

        // Hide the overlay after the fade-out animation completes
        if (!overlayOpen) {
            const timeout = setTimeout(() => setOverlayShow(false), 300);
            return () => clearTimeout(timeout);
        } else {
            setOverlayShow(true);
        }
    }, [overlayOpen, springRef]);

    /**
     * Handles the click event on the overlay. It sets the overlay state to closed.
     */
    const handleClick = () => {
        setOverlayState(false);
    };

    return (
        overlayShow && (
            <animated.div
                className={styles.overlay}
                style={{ opacity, zIndex }}
                onClick={handleClick}
            />
        )
    );
};
